import { ApiError, get, getToken, onAuthChange, post, setToken, type Me } from './api.js';
import { button, clear, el, skeleton, toast } from './ui.js';
import { inviteView, signInView, signUpView } from './views/auth.js';
import { onboardingView } from './views/onboarding.js';
import { leadsView } from './views/leads.js';
import { casesView } from './views/cases.js';
import { lossesView } from './views/losses.js';
import { readinessView } from './views/readiness.js';
import { callReviewView } from './views/callreview.js';
import { quoteView } from './views/quote.js';
import { importView } from './views/importer.js';
import { teamsView } from './views/teams.js';
import { settingsView } from './views/settings.js';
import { dialingView } from './views/dialing.js';
import { inboundView } from './views/inbound.js';
import { todayView } from './views/today.js';
import { intakeView } from './views/intake.js';
import { landingView } from './views/landing.js';
import { installView } from './views/install.js';
import { legalView } from './views/legal.js';
import { initInstall, installState } from './install.js';

const app = document.getElementById('app');
if (!app) throw new Error('Missing #app');

let me: Me | null = null;

/* ------------------------------------------------------------------ theme -- */
const THEME_KEY = 'cv.theme';
function applyTheme(mode: 'system' | 'light' | 'dark'): void {
  if (mode === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', mode);
  try { localStorage.setItem(THEME_KEY, mode); } catch { /* private mode */ }
}
function currentTheme(): 'system' | 'light' | 'dark' {
  try { return (localStorage.getItem(THEME_KEY) as 'system' | 'light' | 'dark') ?? 'system'; } catch { return 'system'; }
}
applyTheme(currentTheme());

/* --------------------------------------------------------------- install -- */
// Registers the service worker and captures the browser's install prompt.
// Called before the first render so the front page can offer a real one-tap
// install rather than pointing at a browser menu.
initInstall();

/* ----------------------------------------------------------------- router -- */
function path(): string {
  const h = location.hash.replace(/^#/, '');
  return h || '/';
}
function go(to: string): void {
  if (path() === to) void render();
  else location.hash = to;
}
window.addEventListener('hashchange', () => void render());
onAuthChange((t) => {
  if (!t) {
    me = null;
    // Stop the bell on sign-out. A timer left running polls a signed-out
    // session every thirty seconds for as long as the tab is open.
    if (winTimer) { clearInterval(winTimer); winTimer = null; }
    winMark = null;
    void render();
  }
});

/* ------------------------------------------------------------- the bell -- */

interface Win {
  id: string;
  agentName: string;
  annualPremiumCents: number;
  occurredAt: string;
}

/**
 * Somebody on the floor just wrote a case, and everybody hears it.
 *
 * WHY IT POLLS RATHER THAN LISTENS
 * Netlify Functions cannot hold a connection open, so a websocket or an
 * event-stream would work perfectly in development and fail silently in
 * production — the worst shape a bug can take. Asking "anything new?" every
 * half minute is one cheap query, works everywhere, and is fast enough for a
 * thing that happens a few times a day.
 *
 * TWO RULES THAT KEEP IT FROM BECOMING NOISE
 *
 * The first poll announces nothing. It only sets the mark. Without that, an
 * agent who opens the app on Monday gets Friday's five deals as five popups,
 * learns that the bell is stale news, and starts ignoring it — which is the
 * one failure a celebration cannot survive.
 *
 * Your own case does not ring for you. You just wrote it and already saw it
 * happen; a popup telling you what you did is the app congratulating itself.
 */
let winMark: string | null = null;
let winTimer: ReturnType<typeof setInterval> | null = null;

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

async function pollWins(): Promise<void> {
  if (!getToken() || !me) { winMark = null; return; }
  try {
    // One template literal, with the query built beside it — never a path
    // assembled by a conditional. A URL that only exists once a branch is taken
    // cannot be found by reading the source, and the wiring test that proves
    // this poll reaches the API at all reads the source. It caught exactly this
    // line.
    const p = new URLSearchParams();
    if (winMark) p.set('since', winMark);
    const res = await get<{ items: Win[]; now: string }>(`/v1/wins?${p}`);
    const first = winMark === null;
    winMark = res.now;
    if (first) return; // The first pass sets the mark and says nothing.

    // Oldest first, so three at once read in the order they happened.
    for (const w of [...res.items].reverse()) {
      if (w.agentName === `${me.user.firstName} ${me.user.lastName}`.trim()) continue;
      toast(`🎉 ${w.agentName} closed a deal — ${money(w.annualPremiumCents)} AP`, 'ok');
    }
  } catch {
    // A missed poll is a missed cheer. It is not worth a red banner, and the
    // next one is thirty seconds away.
  }
}

function startBell(): void {
  if (winTimer) return;
  void pollWins();
  winTimer = setInterval(() => { void pollWins(); }, 30_000);
}

/* The desktop shell fires this from its native menu. */
window.addEventListener('cellvora:navigate', (e) => {
  const to = (e as CustomEvent<{ path: string }>).detail?.path;
  if (to) go(to);
});

async function render(): Promise<void> {
  const p = path();

  if (p.startsWith('/invite')) {
    const token = new URLSearchParams(p.split('?')[1] ?? '').get('token') ?? '';
    if (!token) { mount(errorPage('That invitation link is missing its code. Ask for a new one.')); return; }
    // Straight to the install page, not straight to the leads list. Somebody
    // who has just accepted an invitation is the same "definitely paying
    // attention" moment as somebody who has just created an agency — and it is
    // more likely to matter, because the person joining by invitation is
    // usually an agent holding a phone. They went to /leads before this and
    // were never offered the app at all.
    mount(inviteView(token, () => { me = null; go('/install'); }));
    return;
  }

  // The agency onboarding link. Public on purpose: whoever is filling it in has
  // no account yet, and the token in the URL is the authority.
  if (p.startsWith('/intake/')) {
    mount(intakeView(p.slice('/intake/'.length)));
    return;
  }

  if (!getToken()) {
    // Three public pages: what this is, how to get an account, how to install
    // it. Everything else sends you to sign in.
    if (p === '/' || p === '') mount(landingView(go));
    else if (p === '/install') mount(installView(go));
    // Public, and public whether or not anybody is signed in — see below.
    else if (p === '/legal') mount(legalView(go));
    // Signing up leads to the install page, not straight into the app: the
    // point of creating an agency is to work from it on your own devices, and
    // that is the one moment somebody is definitely paying attention.
    else if (p === '/sign-up') mount(signUpView(() => { me = null; go('/installed'); }, go));
    else mount(signInView(() => { me = null; go('/leads'); }, go));
    return;
  }

  // Straight after sign-up. Same page, but it knows you have just arrived and
  // offers a way on rather than a way in.
  if (p === '/installed') {
    mount(installView(go, { justSignedUp: true, onContinue: () => go('/onboarding') }));
    return;
  }
  if (p === '/install') { mount(installView(go, { onContinue: () => go('/leads') })); return; }

  if (p === '/legal') { mount(legalView(go)); return; }
  /*
   * Signed in, the front door is Today rather than the book.
   *
   * It used to be the list of every lead in the agency: correct, complete, and
   * no help at all at nine in the morning. Everything the platform knew about
   * what today should look like was spread across four screens, and the one
   * somebody opens first was the one that said least.
   */
  if (p === '/' || p === '') { go('/today'); return; }

  if (!me) {
    mount(el('div', { class: 'center' }, skeleton(5)));
    try {
      me = await get<Me>('/v1/me');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { setToken(null); void render(); return; }
      mount(errorPage(e instanceof ApiError ? e.message : 'Could not load your account.'));
      return;
    }
  }
  // Signed in and loaded — the floor can be heard from any screen.
  startBell();

  // Somebody who has not finished setting themselves up goes there first,
  // rather than landing on an empty desk with no idea what to do.
  // Somebody mid-setup is sent to the checklist from the front door only.
  // The steps link out to /teams, /import and the rest, so trapping every
  // route here would make the checklist's own buttons do nothing.
  const needsOnboarding = !me.user.onboardedAt;
  if (needsOnboarding && (p === '/' || p === '')) {
    go('/onboarding');
    return;
  }

  const view = routeView(p);
  mount(chrome(view));
}

/**
 * Who may open "Ready to work". Mirrors the API, which checks the ROLE.
 *
 * Kept as one function used by both the nav and the route, so the link and the
 * screen can never disagree — a link that 403s is worse than no link.
 */
const READINESS_ROLES = new Set(['owner', 'admin', 'manager', 'compliance']);
function canSeeReadiness(m: Me): boolean {
  return READINESS_ROLES.has(m.user.role);
}

function routeView(p: string): HTMLElement {
  const m = me!;
  if (p.startsWith('/settings')) {
    const tab = p.split('/')[2] ?? 'security';
    // Dropping the cached account is the point: a settings panel that shows
    // server state has to re-read it after changing it, and `go` to the tab you
    // are already on redraws from the copy fetched before the change.
    return settingsView(m, tab, go, () => { me = null; void render(); });
  }
  if (p.startsWith('/quote')) return quoteView(m, go);
  if (p.startsWith('/cases')) return casesView(m, p.split('/')[2] ?? 'board', go);
  if (p.startsWith('/today')) return todayView(m, go);
  if (p.startsWith('/dialing')) return dialingView(m, go);
  if (p.startsWith('/inbound')) return inboundView(m, go);
  if (p.startsWith('/teams')) return teamsView(m);
  if (p.startsWith('/import')) {
    return m.capabilities.includes('lead.import')
      ? importView(go)
      : errorPage('Importing leads is a manager\'s job. Ask yours to bring the list in.');
  }
  if (p.startsWith('/compliance')) return complianceView();
  if (p.startsWith('/losses')) {
    return m.capabilities.includes('user.manage')
      ? lossesView(go)
      : errorPage('The loss reports are a manager\'s view. Yours is on the calling screen.');
  }
  /*
   * THE ROLE, NOT `capabilities`, AND THIS IS NOT A SHORTCUT.
   *
   * The route checks `can(role, 'report.run')` on the role alone, because
   * `requireCap` also grants a team LEADER that capability and an agent leading
   * a desk would otherwise get the whole agency's roster and book. But
   * `capabilities` in /v1/me IS computed with the leader grant — so checking it
   * here would put the link in front of that same agent and hand them a 403
   * when they pressed it. Nav and route agree by asking the same question.
   */
  if (p.startsWith('/readiness')) {
    return canSeeReadiness(m)
      ? readinessView(go)
      : errorPage('Who is licensed where is a manager\'s view. Yours is on the calling screen.');
  }
  if (p === '/onboarding') return onboardingView(m, () => { me = null; void render(); }, go);
  /*
   * `/leads/<id>/calls` — every call this household has had.
   *
   * A sub-path of the lead rather than a nav entry, because it is only ever
   * reached FROM a lead and the top bar has no room left: it fits eleven links
   * and has eleven. See the note on `.tb-org` in styles.css.
   *
   * Checked before the `/leads/<id>` split below, which would otherwise swallow
   * it and open the lead record instead.
   */
  if (p.startsWith('/leads/') && p.endsWith('/calls')) {
    return callReviewView(p.slice('/leads/'.length, -'/calls'.length), go);
  }

  // `/leads/<id>` opens that lead. Anything else on /leads is the list.
  const leadId = p.startsWith('/leads/') ? p.slice('/leads/'.length).split('/')[0] : undefined;
  return leadsView(m, go, leadId || undefined);
}

/** Running a scrub is an operational step, so it has a screen of its own. */
function complianceView(): HTMLElement {
  const host = el('div');
  const run = button('Run a do-not-call scrub on the whole book', 'pri');
  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Compliance' }),
    el('h1', { text: 'Scrub' }),
    el('p', { text: 'A freshly bought list arrives blocked: every number needs a do-not-call scrub before the gate will let it be dialled. In production your DNC vendor calls this; here you can record one by hand.' })));

  /*
   * The half this screen does not do, said here because this is where somebody
   * looks for it.
   *
   * A scrub compares numbers against the registry files this agency has
   * loaded. With none loaded it compares them against nothing and every number
   * still fails the dial check — correctly, because a number nobody looked up
   * is not a number that came back clear. The loader lives in Settings, and
   * anybody who came to this screen looking for it found a single button that
   * did not mention it.
   */
  host.append(el('section', { class: 'panel' },
    el('div', { class: 'note' },
      el('b', null, 'A scrub checks against the registry files you have loaded. '),
      'If none are loaded it checks against nothing, and every number keeps failing the '
      + 'dial check. Loading them — and seeing which area codes your book actually needs — '
      + 'is in Settings.'),
    el('div', { style: 'margin-top:12px' },
      button('Open the registry files', 'sm', () => go('/settings/compliance')))));
  const out = el('div');
  /*
   * The scrub arrives in batches, and this keeps asking until the book is done.
   *
   * The server can only do a bounded slice per request — a single request over
   * 300,000 leads is a request that gets killed halfway, which is how a book
   * ends up half-scrubbed with nobody aware of it. So the loop lives here,
   * where there is a screen to show progress on, and each round says how many
   * are left rather than leaving somebody watching a spinner.
   *
   * If a batch fails, what has already been written stays written: a scrub is
   * resumable by construction, so pressing the button again continues instead
   * of starting over.
   */
  run.addEventListener('click', async () => {
    run.disabled = true;
    clear(out);
    const progress = el('div', { class: 'note' }, 'Scrubbing…');
    out.append(progress);
    let scrubbed = 0;
    let suppressed = 0;
    let since: string | undefined;
    let uncovered: Array<{ areaCode: string; count: number; coverage: string }> = [];
    try {
      for (let round = 0; ; round += 1) {
        run.textContent = round === 0 ? 'Scrubbing…' : `Scrubbing… ${scrubbed.toLocaleString()} done`;
        const res = await post<{
          scrubbed: number; suppressed: number; done: boolean; remaining: number; since: string;
          uncovered?: Array<{ areaCode: string; count: number; coverage: string }>;
        }>('/v1/compliance/scrub', since ? { since } : {});
        scrubbed += res.scrubbed;
        suppressed += res.suppressed;
        since = res.since;
        uncovered = res.uncovered ?? [];
        if (res.done) break;
        clear(progress);
        progress.append(
          el('b', null, `${scrubbed.toLocaleString()} scrubbed. `),
          `${res.remaining.toLocaleString()} to go…`,
        );
        // A book that reports leads outstanding but scrubbed none of them would
        // otherwise spin forever. Stop and say so instead.
        if (res.scrubbed === 0) {
          throw new Error(`The scrub stopped with ${res.remaining.toLocaleString()} records left.`);
        }
      }
      clear(out);
      /*
       * THE HONEST HALF OF THE ANSWER, WHICH THIS SCREEN USED TO THROW AWAY.
       *
       * The server has always returned `uncovered` — the area codes in the book
       * that no downloaded registry file covers. Nothing was checked for those
       * numbers, because there was nothing to check them against.
       *
       * Showing only "none came back listed" while sitting on that list is the
       * exact failure the whole registry design exists to prevent: import
       * 300,000 purchased numbers, press scrub, watch the file go green, and
       * never once consult the federal registry. Worse, the dial gate correctly
       * keeps refusing every one of them afterwards — so the screen says the
       * scrub worked, the calling floor says nothing is callable, and the two
       * appear to disagree. They do not. Only one of them was telling the truth.
       */
      const blind = uncovered.reduce((n, u) => n + u.count, 0);
      out.append(el('div', { class: blind ? 'warnbox' : 'okbox' },
        el('b', null, `${scrubbed.toLocaleString()} records scrubbed. `),
        suppressed ? `${suppressed} came back listed and were suppressed. ` : 'None came back listed. ',
        ...(blind ? [
          el('div', { style: 'margin-top:8px' },
            el('b', null, `${blind.toLocaleString()} of them were checked against nothing. `),
            'You hold no current registry file for '
            + uncovered.slice(0, 6).map((u) => `${u.areaCode} (${u.count.toLocaleString()})`).join(', ')
            + (uncovered.length > 6 ? `, and ${uncovered.length - 6} more area codes` : '')
            + '. Those numbers stay blocked at dial time, which is correct — a number nobody '
            + 'looked up is not a number that came back clear.'),
          el('div', { style: 'margin-top:10px' },
            button('Load the registry files', 'sm', () => go('/settings/compliance'))),
        ] : [])));
      // 'info', not 'crit': an uncovered area code is a finding about what the
      // agency has bought, not a failure of the scrub. The box says the rest.
      toast(blind ? 'Scrub recorded — some area codes have no registry file.' : 'Scrub recorded.',
        blind ? 'info' : 'ok');
    } catch (e) {
      clear(out);
      const msg = e instanceof ApiError ? e.message
        : e instanceof Error ? e.message : 'Could not record the scrub.';
      out.append(el('div', { class: 'warnbox' },
        el('b', null, `${msg} `),
        scrubbed
          ? `${scrubbed.toLocaleString()} were scrubbed before it stopped, and they stay scrubbed — run it again to carry on.`
          : 'Nothing was scrubbed. Run it again.'));
      toast(msg, 'crit');
    } finally {
      run.disabled = false;
      run.textContent = 'Run a do-not-call scrub on the whole book';
    }
  });
  host.append(el('section', { class: 'panel' }, run, el('div', { style: 'margin-top:12px' }, out)));
  return host;
}

/**
 * The page shown when boot itself failed.
 *
 * It is mounted OUTSIDE `chrome()`, so there is no navigation on screen and no
 * other control of any kind — which meant a transient network blip while
 * fetching `/v1/me` stranded somebody on a sentence with nothing to press. The
 * only way out was to know to reload the browser, and the message did not say
 * so.
 *
 * Two ways forward, because they fail for different reasons: retrying is right
 * for a blip, and signing out is right for a session the server no longer
 * recognises in some way that is not a clean 401.
 */
function errorPage(message: string): HTMLElement {
  return el('div', { class: 'center' },
    el('div', { class: 'warnbox' },
      message,
      el('div', { style: 'display:flex;gap:8px;margin-top:12px;flex-wrap:wrap' },
        button('Try again', 'sm pri', () => { void render(); }),
        button('Sign out', 'sm ghost', () => { setToken(null); void render(); }))));
}

/* ------------------------------------------------------------------ chrome -- */
function chrome(view: HTMLElement): HTMLElement {
  const m = me!;
  const links: Array<[string, string, boolean, string]> = [
    ['/today', 'Today', true, NAV.today],
    ['/dialing', 'Dialling', true, NAV.dialing],
    ['/inbound', 'Inbound', true, NAV.inbound],
    ['/leads', 'Leads', true, NAV.leads],
    ['/quote', 'Quote', true, NAV.quote],
    ['/cases', 'Cases', true, NAV.cases],
    ['/teams', 'Teams', true, NAV.teams],
    ['/import', 'Import', m.capabilities.includes('lead.import'), NAV.import],
    // "Ready to work" is the page's name; the nav label is shorter on purpose —
    // the rail is a fixed width and the bottom bar on a phone is 46px a tab.
    ['/readiness', 'Readiness', canSeeReadiness(m), NAV.readiness],
    ['/losses', 'Why we lose', m.capabilities.includes('user.manage'), NAV.losses],
    ['/compliance', 'Compliance', m.capabilities.includes('dnc.write'), NAV.compliance],
    ['/settings/security', 'Settings', true, NAV.settings],
  ];
  const p = path();
  /*
   * The sections, as one list rendered three ways by the stylesheet: a rail
   * down the left on a desktop, an icon strip on a tablet, a tab bar along the
   * bottom of a phone. It sits OUTSIDE the header on purpose. On a phone the
   * bar is `position: fixed`, and a `backdrop-filter` on any ancestor makes
   * that ancestor the containing block, which once pinned the tab bar inside
   * a 52px header. Outside the header there is no ancestor to trap it.
   *
   * The icon is decoration for a sighted reader; the label is the name, and
   * it stays in the DOM at every width so a screen reader never gets a bare
   * glyph.
   */
  const nav = el('nav', { class: 'nav', 'aria-label': 'Sections' });
  for (const [href, label, ok, icon] of links) {
    if (!ok) continue;
    /*
     * Three groups, so twelve links read as a glance rather than a scan: the
     * work of the day, the book, and the agency around it. Each group opens
     * with a section every role can see, so a heading is never left alone.
     * The rail shows the words; the phone bar hides them.
     */
    const group = NAV_GROUP[href];
    if (group) nav.append(el('span', { class: 'nav-group', text: group, 'aria-hidden': 'true' }));
    const active = p === href || (href !== '/leads' && p.startsWith(href.split('/').slice(0, 2).join('/')));
    nav.append(el('a', {
      href: `#${href}`,
      'aria-current': active ? 'page' : null,
      title: label,
      onclick: (e: Event) => { e.preventDefault(); go(href); },
    },
    el('span', { class: 'nav-ico', html: icon }),
    el('span', { class: 'nav-txt', text: label })));
  }

  // A permanent way to reach the install page. Without it the only routes were
  // the public front page and the seconds right after signing up, so anybody
  // who joined by invitation, or said "later" once, could never find it again.
  // Hidden when we are already running as the installed app, where it would be
  // an offer to do something already done.
  const showInstall = installState().platform !== 'installed';

  /*
   * The top bar's controls, as icon-and-label pairs.
   *
   * On a phone the label is hidden and only the icon shows. Three text buttons
   * plus the wordmark do not fit a 375px screen — they pushed every page
   * sideways by up to 37 pixels on a folded Galaxy, which is the whole
   * application scrolling horizontally on every screen. Shrinking to icons
   * keeps all three functions rather than dropping one, and the label stays in
   * `aria-label` and `title` so it is still announced and still hoverable.
   */
  const barBtn = (label: string, svg: string, onclick: () => void): HTMLElement =>
    el('button', {
      class: 'btn sm ghost tb-btn', type: 'button', onclick,
      'aria-label': label, title: label,
    },
    el('span', { class: 'tb-ico', html: svg }),
    el('span', { class: 'tb-txt', text: label }));

  const theme = currentTheme();
  const themeBtn = barBtn(theme === 'dark' ? 'Day' : 'Night', theme === 'dark' ? SUN : MOON, () => {
    applyTheme(theme === 'dark' ? 'light' : 'dark');
    void render();
  });

  return el('div', { class: 'shell' },
    el('header', { class: 'topbar' },
      el('a', { class: 'brand', href: '#/leads', onclick: (e: Event) => { e.preventDefault(); go('/leads'); } },
        el('span', { html: MARK }),
        el('b', null, 'CELLVORA')),
      el('span', { class: 'spacer' }),
      // Classed so the stylesheet can cap it at every width and drop it on a
      // phone — see the note on `.tb-org`. It is the one decorative thing in
      // this bar.
      el('span', { class: 'tb-org', text: m.org?.name ?? '' }),
      showInstall ? barBtn('Get the app', DOWNLOAD, () => go('/install')) : null,
      themeBtn,
      barBtn('Sign out', SIGNOUT, async () => {
        try { await post('/v1/auth/sign-out'); } catch { /* the token is dropped either way */ }
        setToken(null);
        me = null;
        go('/');   // back to the public front page
      })),
    nav,
    el('main', null, view));
}

const ICO = (d: string): string =>
  `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
     stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;

const DOWNLOAD = ICO('<path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M4 20h16"/>');

/* Where each group of sections begins. */
const NAV_GROUP: Record<string, string> = { '/today': 'Work', '/leads': 'Book', '/teams': 'Agency' };

/* One glyph per section. Literal markup written here, never data. */
const PHONE = '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.6a2 2 0 0 1-.5 2.1L8.1 9.7a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.8.3 1.7.6 2.6.7a2 2 0 0 1 1.9 2z"/>';
const NAV = {
  today: ICO('<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>'),
  dialing: ICO(PHONE),
  inbound: ICO(`${PHONE}<path d="M16 2v6h6"/><path d="m22 2-6 6"/>`),
  leads: ICO('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.9"/><path d="M16 3.1a4 4 0 0 1 0 7.8"/>'),
  quote: ICO('<path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2z"/><path d="M8 8h8M8 12h8M8 16h5"/>'),
  cases: ICO('<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>'),
  teams: ICO('<circle cx="9" cy="7" r="4"/><path d="M3 21v-2a6 6 0 0 1 12 0v2"/><circle cx="18" cy="9" r="3"/><path d="M21 21v-1.5a4.5 4.5 0 0 0-3-4.2"/>'),
  import: ICO('<path d="M12 3v12"/><path d="m7 8 5-5 5 5"/><path d="M4 20h16"/>'),
  readiness: ICO('<circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.5 2.5 4.5-5"/>'),
  losses: ICO('<path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/>'),
  compliance: ICO('<path d="M12 22s8-3 8-10V5l-8-3-8 3v7c0 7 8 10 8 10z"/>'),
  settings: ICO('<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3"/><path d="M1 14h6M9 8h6M17 16h6"/>'),
};
const MOON = ICO('<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z"/>');
const SUN = ICO('<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>');
const SIGNOUT = ICO('<path d="M15 17l5-5-5-5"/><path d="M20 12H9"/><path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3"/>');

const MARK = `<svg viewBox="0 0 48 48" width="26" height="26" aria-hidden="true">
  <defs><linearGradient id="cvb" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stop-color="var(--copper)"/><stop offset="1" stop-color="var(--crit)"/>
  </linearGradient></defs>
  <path d="M36.63 12.63 A17 17 0 1 0 36.63 35.37" fill="none" stroke="url(#cvb)" stroke-width="5.4" stroke-linecap="round"/>
  <path d="M29.45 16.22 A9.5 9.5 0 1 0 29.45 31.78" fill="none" stroke="url(#cvb)" stroke-width="3.9" stroke-linecap="round"/>
  <circle cx="24" cy="24" r="3.5" fill="url(#cvb)"/>
  <circle cx="41.6" cy="24" r="2.3" fill="var(--copper)"/>
  <circle cx="46.4" cy="24" r="1.3" fill="var(--copper)" opacity=".5"/>
</svg>`;

function mount(node: HTMLElement): void {
  clear(app!);
  app!.append(node);
  window.scrollTo({ top: 0 });
}

void render();
